/**
 * WebSocket Context - React context for real-time communication
 * Provides WebSocket connection management and event handling for the entire app
 */

import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContext';
import { debugLog } from '../utils/debugLog';

const WebSocketContext = createContext();

// WebSocket connection states
export const CONNECTION_STATES = {
  DISCONNECTED: 'disconnected',
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  RECONNECTING: 'reconnecting',
  ERROR: 'error'
};

// WebSocket provider component
export const WebSocketProvider = ({ children }) => {
  const [connectionState, setConnectionState] = useState(CONNECTION_STATES.DISCONNECTED);
  const [connectedUsers, setConnectedUsers] = useState(0);
  const [error, setError] = useState(null);
  const [lastActivity, setLastActivity] = useState(null);
  const [socketConnected, setSocketConnected] = useState(false);

  const socketRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const heartbeatIntervalRef = useRef(null);
  const eventListenersRef = useRef(new Map());
  const isConnectingRef = useRef(false); // Track if currently connecting
  const isIntentionalDisconnectRef = useRef(false); // Track manual disconnect
  const authRetryCountRef = useRef(0); // guard against infinite auth-error loops
  const reconnectAttemptsRef = useRef(0); // Track reconnection attempts
  const lastConnectTimeRef = useRef(0); // Track last connection attempt time

  // Get authentication state from AuthContext
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth();

  // Derive WS base URL from REACT_APP_API_URL (strips the /api path suffix).
  // Fall back to the hard-coded Render backend URL when the env var is missing
  // so we never accidentally target the Vercel hostname on port 5000.
  const WS_CONFIG = {
    url: process.env.REACT_APP_WS_URL ||
      (process.env.REACT_APP_API_URL
        ? process.env.REACT_APP_API_URL.replace(/\/api(\/v\d+)?$/, '')
        : 'https://api.sportifide.com'),
    options: {
      transports: ['websocket', 'polling'],
      timeout: 20000,
      reconnection: false, // Disable Socket.IO's auto-reconnection, we handle it manually
      autoConnect: false, // Don't auto-connect, we control when to connect
      reconnectionDelay: 5000, // Increase delay if reconnection happens
      reconnectionDelayMax: 30000 // Max delay to prevent rapid retries
    }
  };

  debugLog('🔌 WebSocketContext: Provider initialized (v2.0 - Auth-based connection)');
  debugLog('🔐 Authentication state:', { isAuthenticated, isAuthLoading });

  // Connect to WebSocket server
  const connect = useCallback((demoMode = false) => {
    // For demo mode, skip auth check
    if (!demoMode) {
      if (isAuthLoading) {
        debugLog('⏳ WebSocket: Auth still loading, delaying connection');
        return;
      }

      if (!isAuthenticated) {
        debugLog('⚠️ WebSocket: Cannot connect - not authenticated');
        setError('Authentication required for WebSocket connection');
        return;
      }
    }

    // Prevent multiple simultaneous connection attempts
    if (isConnectingRef.current) {
      debugLog('⏳ Connection already in progress, skipping...');
      return;
    }

    // Prevent rapid reconnection attempts (debounce 2 seconds)
    const now = Date.now();
    if (now - lastConnectTimeRef.current < 2000) {
      debugLog('⏱️ Debouncing connection attempt...');
      return;
    }
    lastConnectTimeRef.current = now;

    if (socketRef.current?.connected) {
      debugLog('✅ Socket already connected, skipping...');
      return;
    }

    // Clean up existing socket before creating new one
    if (socketRef.current) {
      debugLog('🧹 Cleaning up existing socket...');
      socketRef.current.removeAllListeners();
      socketRef.current.disconnect();
      socketRef.current = null;
    }

    isConnectingRef.current = true;
    isIntentionalDisconnectRef.current = false;
    setConnectionState(CONNECTION_STATES.CONNECTING);
    setError(null);

    debugLog('🔌 Initiating WebSocket connection...');

    // Create socket connection
    // Auth via HttpOnly cookies (sent automatically by browser)
    const authConfig = demoMode ? { demo: true } : {};
    
    socketRef.current = io(WS_CONFIG.url, {
      ...WS_CONFIG.options,
      withCredentials: true, // Send cookies for authentication
      auth: authConfig
    });

    // Connection event handlers
    socketRef.current.on('connect', handleConnect);
    socketRef.current.on('disconnect', handleDisconnect);
    socketRef.current.on('connect_error', handleConnectError);
    socketRef.current.on('reconnect', handleReconnect);
    socketRef.current.on('reconnect_attempt', handleReconnectAttempt);
    socketRef.current.on('reconnect_error', handleReconnectError);
    socketRef.current.on('reconnect_failed', handleReconnectFailed);

    // Custom event handlers
    socketRef.current.on('connected', handleServerConnected);
    socketRef.current.on('pong', handlePong);
    socketRef.current.on('error', handleServerError);

    // Manually initiate connection (autoConnect is false)
    socketRef.current.connect();

  }, [isAuthenticated, isAuthLoading]);

  // Disconnect from WebSocket server
  const disconnect = useCallback(() => {
    debugLog('🔌 Intentionally disconnecting WebSocket...');
    isIntentionalDisconnectRef.current = true;
    isConnectingRef.current = false;

    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (socketRef.current) {
      socketRef.current.removeAllListeners();
      socketRef.current.disconnect();
      socketRef.current = null;
    }

    setConnectionState(CONNECTION_STATES.DISCONNECTED);
    reconnectAttemptsRef.current = 0;
  }, []);

  // Event handlers
  const handleConnect = () => {
    debugLog('✅ WebSocket connected successfully');
    isConnectingRef.current = false;
    reconnectAttemptsRef.current = 0;
    authRetryCountRef.current = 0; // reset auth-retry counter on successful connect
    setConnectionState(CONNECTION_STATES.CONNECTED);
    setSocketConnected(true);
    setError(null);
    setLastActivity(new Date());
    
    // Start heartbeat
    startHeartbeat();
  };

  const handleDisconnect = (reason) => {
    console.error('❌ WebSocket disconnected:', reason);
    console.trace('Stack trace for disconnect');
    isConnectingRef.current = false;
    setConnectionState(CONNECTION_STATES.DISCONNECTED);
    setSocketConnected(false);
    
    // Stop heartbeat
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }

    // Don't auto-reconnect if:
    // 1. It was an intentional disconnect
    // 2. Too many reconnect attempts (prevent infinite loops)
    if (isIntentionalDisconnectRef.current) {
      debugLog('🛑 Intentional disconnect - not reconnecting');
      return;
    }

    if (reconnectAttemptsRef.current >= 10) {
      console.error('🚫 Max reconnection attempts reached - stopping');
      setError('Max reconnection attempts reached. Please refresh the page.');
      return;
    }

    // Auto-reconnect only for unexpected disconnections
    if (reason !== 'io client disconnect' && reason !== 'io server disconnect') {
      reconnectAttemptsRef.current++;
      debugLog(`🔄 Scheduling reconnection attempt ${reconnectAttemptsRef.current}/10...`);
      scheduleReconnect();
    }
  };

  const handleConnectError = (error) => {
    console.error('⚠️ WebSocket connection error:', error.message);
    isConnectingRef.current = false;
    setConnectionState(CONNECTION_STATES.ERROR);
    setError(error.message);

    if (error.message === 'Authentication required' || error.message === 'Authentication failed') {
      // Auth error: the login session is no longer valid (e.g. token expired).
      // Set error state — the user can log out/in to get fresh cookies.
      // The polling fallback in useLiveMatchWebSocket handles score updates while down.
      console.warn('🛑 WS auth error — session may have expired. Please log out and back in.');
      setConnectionState(CONNECTION_STATES.ERROR);
      return;
    }

    // Check if we should attempt reconnection
    if (reconnectAttemptsRef.current < 10 && !isIntentionalDisconnectRef.current) {
      reconnectAttemptsRef.current++;
      scheduleReconnect();
    } else {
      console.error('🚫 Not scheduling reconnect - max attempts or intentional disconnect');
    }
  };

  const handleReconnect = (attemptNumber) => {
    setConnectionState(CONNECTION_STATES.CONNECTED);
    setError(null);
  };

  const handleReconnectAttempt = (attemptNumber) => {
    setConnectionState(CONNECTION_STATES.RECONNECTING);
  };

  const handleReconnectError = (error) => {
    setConnectionState(CONNECTION_STATES.ERROR);
    setError(`Reconnection failed: ${error.message}`);
  };

  const handleReconnectFailed = () => {
    setConnectionState(CONNECTION_STATES.ERROR);
    setError('Failed to reconnect after multiple attempts');
  };

  const handleServerConnected = (data) => {
    debugLog('🎉 Server welcome message:', data.message);
    setLastActivity(new Date());
  };

  const handlePong = (data) => {
    setLastActivity(new Date(data.timestamp));
  };

  const handleServerError = (error) => {
    setError(error.message || 'Server error occurred');
  };

  // Start heartbeat to keep connection alive
  const startHeartbeat = () => {
    if (heartbeatIntervalRef.current) return;

    heartbeatIntervalRef.current = setInterval(() => {
      if (socketRef.current?.connected) {
        socketRef.current.emit('ping');
      }
    }, 30000); // Ping every 30 seconds
  };

  // Schedule reconnection attempt
  const scheduleReconnect = () => {
    if (reconnectTimeoutRef.current) {
      debugLog('⏳ Reconnect already scheduled, skipping...');
      return;
    }

    if (isIntentionalDisconnectRef.current) {
      debugLog('🛑 Intentional disconnect - not scheduling reconnect');
      return;
    }

    // Exponential backoff: 3s, 6s, 12s, 24s, max 30s
    const delay = Math.min(3000 * Math.pow(2, reconnectAttemptsRef.current - 1), 30000);
    debugLog(`⏰ Scheduling reconnect in ${delay}ms...`);

    reconnectTimeoutRef.current = setTimeout(() => {
      debugLog('🔄 Attempting reconnection...');
      setConnectionState(CONNECTION_STATES.RECONNECTING);
      connect();
      reconnectTimeoutRef.current = null;
    }, delay);
  };

  // Join match room for real-time updates
  const joinMatch = useCallback((matchId) => {
    if (!socketRef.current?.connected) {
      return false;
    }

    socketRef.current.emit('join_match', { matchId });
    return true;
  }, []);

  // Leave match room
  const leaveMatch = useCallback((matchId) => {
    if (!socketRef.current?.connected) {
      return false;
    }

    socketRef.current.emit('leave_match', { matchId });
    return true;
  }, []);

  // Join tournament room
  const joinTournament = useCallback((tournamentId) => {
    if (!socketRef.current?.connected) {
      return false;
    }

    socketRef.current.emit('join_tournament', { tournamentId });
    return true;
  }, []);

  // Leave tournament room
  const leaveTournament = useCallback((tournamentId) => {
    if (!socketRef.current?.connected) {
      return false;
    }

    socketRef.current.emit('leave_tournament', { tournamentId });
    return true;
  }, []);

  // Join league room
  const joinLeague = useCallback((leagueId) => {
    if (!socketRef.current?.connected) {
      return false;
    }

    socketRef.current.emit('join_league', { leagueId });
    return true;
  }, []);

  // Leave league room
  const leaveLeague = useCallback((leagueId) => {
    if (!socketRef.current?.connected) {
      return false;
    }

    socketRef.current.emit('leave_league', { leagueId });
    return true;
  }, []);

  // Send score update
  const sendScoreUpdate = useCallback((matchId, scoreData, eventType = 'score_update') => {
    if (!socketRef.current?.connected) {
      return false;
    }

    socketRef.current.emit('score_update', {
      matchId,
      scoreData,
      eventType
    });
    return true;
  }, []);

  // Send match event
  const sendMatchEvent = useCallback((matchId, eventType, eventData) => {
    if (!socketRef.current?.connected) {
      return false;
    }

    socketRef.current.emit('match_event', {
      matchId,
      eventType,
      eventData
    });
    return true;
  }, []);

  // Add event listener
  const addEventListener = useCallback((eventName, handler) => {
    if (!socketRef.current || !socketRef.current.connected) {
      return () => {};
    }

    
    // Store handler reference for cleanup
    if (!eventListenersRef.current.has(eventName)) {
      eventListenersRef.current.set(eventName, new Set());
    }
    eventListenersRef.current.get(eventName).add(handler);

    socketRef.current.on(eventName, handler);

    // Return cleanup function
    return () => {
      if (socketRef.current) {
        socketRef.current.off(eventName, handler);
      }
      const listeners = eventListenersRef.current.get(eventName);
      if (listeners) {
        listeners.delete(handler);
        if (listeners.size === 0) {
          eventListenersRef.current.delete(eventName);
        }
      }
    };
  }, []);

  // Remove event listener
  const removeEventListener = useCallback((eventName, handler) => {
    if (socketRef.current) {
      socketRef.current.off(eventName, handler);
    }
    
    const listeners = eventListenersRef.current.get(eventName);
    if (listeners) {
      listeners.delete(handler);
      if (listeners.size === 0) {
        eventListenersRef.current.delete(eventName);
      }
    }
  }, []);

  // Get connection status
  const getConnectionStatus = useCallback(() => {
    return {
      state: connectionState,
      connected: socketRef.current?.connected || false,
      error,
      lastActivity,
      connectedUsers
    };
  }, [connectionState, error, lastActivity]);

  // Initialize connection on mount (only if authenticated)
  useEffect(() => {
    // DISABLED: Don't auto-connect on mount to prevent connection spam
    // Connection should be initiated explicitly after login via AuthContext
    // const authenticated = isAuthenticated();
    // if (authenticated) {
    //   connect();
    // }

    // Cleanup on unmount
    return () => {
      disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - only run once on mount

  // Connect/disconnect based on authentication state from AuthContext
  useEffect(() => {
    debugLog('🔐 WebSocket: Auth state changed check - isAuthenticated:', isAuthenticated, 'isAuthLoading:', isAuthLoading);

    if (isAuthLoading) {
      debugLog('⏳ WebSocket: Auth still loading, skipping connect/disconnect');
      return;
    }
    
    // Only connect if authenticated AND not already connected AND not currently connecting
    if (isAuthenticated && !socketRef.current?.connected && !isConnectingRef.current) {
      debugLog('✅ User authenticated AND socket not connected - initiating WebSocket');
      connect();
    } 
    // Only disconnect if NOT authenticated AND socket is currently connected
    else if (!isAuthenticated && socketRef.current?.connected) {
      debugLog('❌ User logged out - disconnecting WebSocket');
      disconnect();
    }
    else {
      debugLog('⏭️ WebSocket: Skipping action', {
        isAuthenticated,
        socketConnected: socketRef.current?.connected,
        isConnecting: isConnectingRef.current
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isAuthLoading, connect, disconnect]);

  // Listen for authentication changes
  useEffect(() => {
    // DISABLED: Don't auto-connect via storage listener to prevent connection spam
    // Use explicit connect() calls from AuthContext after login instead
    /*
    const handleStorageChange = () => {
      const authenticated = isAuthenticated();
      
      if (authenticated && !socketRef.current?.connected) {
        connect();
      } else if (!authenticated && socketRef.current?.connected) {
        disconnect();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
    */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - listener manages its own state

  // Connect in demo mode (no authentication required)
  const connectDemo = useCallback(() => {
    connect(true);
  }, [connect]);

  // Context value
  const contextValue = {
    // Connection management
    connect,
    connectDemo,
    disconnect,
    getConnectionStatus,
    
    // Room management
    joinMatch,
    leaveMatch,
    joinTournament,
    leaveTournament,
    joinLeague,
    leaveLeague,
    
    // Event handling
    sendScoreUpdate,
    sendMatchEvent,
    addEventListener,
    removeEventListener,
    
    // State
    socket: socketRef.current,
    connectionState,
    connected: socketConnected && connectionState === CONNECTION_STATES.CONNECTED,
    error,
    lastActivity,
    connectedUsers,
    
    // Constants
    CONNECTION_STATES
  };

  return (
    <WebSocketContext.Provider value={contextValue}>
      {children}
    </WebSocketContext.Provider>
  );
};

// Hook to use WebSocket context
export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  
  return context;
};

// Hook for match-specific WebSocket functionality
// numericMatchId: the integer DB id (match.id) — used for room join and event
// filtering.  Falls back to matchId (URL param) only when not yet available.
export const useMatchWebSocket = (matchId, numericMatchId) => {
  const webSocket = useWebSocket();
  const [matchEvents, setMatchEvents] = useState([]);
  const [liveScore, setLiveScore] = useState(null);
  // The whole live_score_update broadcast (eventId, eventData with points /
  // side / period) so viewers can show the real event and dedupe on eventId
  const [lastScoreBroadcast, setLastScoreBroadcast] = useState(null);
  // Server match clock (LS-B09), from 'clock_update' broadcasts
  const [clockState, setClockState] = useState(null);
  const [matchStatus, setMatchStatus] = useState('unknown');
  const [hasJoined, setHasJoined] = useState(false);

  // effectiveId is the integer match ID once the match has loaded, otherwise
  // we hold off joining until it is available so we join the correct room.
  const effectiveId = numericMatchId != null ? String(numericMatchId) : null;

  // Join match room — only once the integer ID is available
  useEffect(() => {
    if (!effectiveId || !webSocket.connected || hasJoined) return;

    debugLog(`🎯 Joining match room ${effectiveId} (useMatchWebSocket)`);
    const success = webSocket.joinMatch(effectiveId);
    if (success) setHasJoined(true);

    return () => {
      if (hasJoined) {
        debugLog(`🚪 Leaving match room ${effectiveId} (useMatchWebSocket cleanup)`);
        webSocket.leaveMatch(effectiveId);
        setHasJoined(false);
      }
    };
  }, [effectiveId, webSocket.connected, hasJoined]); // Add hasJoined to dependencies

  // Listen for match events (only when connected)
  useEffect(() => {
    if (!matchId || !webSocket.connected) return;

    debugLog(`🎧 Setting up event listeners for match ${matchId}`);

    // Compare using numeric ID so the comparison works regardless of whether
    // matchId is a UUID, short_code, or integer string in the URL.
    const sameMatch = (incomingId) =>
      effectiveId != null
        ? parseInt(incomingId) === parseInt(effectiveId)
        : parseInt(incomingId) === parseInt(matchId);

    const removeScoreListener = webSocket.addEventListener('live_score_update', (data) => {
      if (sameMatch(data.matchId)) {
        setLiveScore(data.scoreData);
        setLastScoreBroadcast(data);
        // Don't add to matchEvents to prevent duplicate event creation in LiveMatch component
      }
    });

    const removeClockListener = webSocket.addEventListener('clock_update', (data) => {
      if (sameMatch(data.matchId) && data.clockState) {
        setClockState(data.clockState);
      }
    });

    const removeEventListener = webSocket.addEventListener('match_event', (data) => {
      if (sameMatch(data.matchId)) {
        debugLog('🎯 Match event:', data);
        setMatchEvents(prev => [data, ...prev.slice(0, 49)]);
      }
    });

    const removeStatusListener = webSocket.addEventListener('match_status_update', (data) => {
      if (sameMatch(data.matchId)) {
        debugLog('📊 Match status update:', data);
        setMatchStatus(data.status);
      }
    });

    const removeEndListener = webSocket.addEventListener('match_ended', (data) => {
      if (sameMatch(data.matchId)) {
        debugLog('🏁 Match ended:', data);
        setMatchStatus('completed');
        setMatchEvents(prev => [{
          eventType: 'match_ended',
          eventData: data,
          timestamp: data.timestamp
        }, ...prev.slice(0, 49)]);
      }
    });

    return () => {
      removeScoreListener();
      removeClockListener();
      removeEventListener();
      removeStatusListener();
      removeEndListener();
    };
  }, [matchId, effectiveId, webSocket]); // re-attach when numeric ID resolves or webSocket changes

  return {
    matchEvents,
    liveScore,
    lastScoreBroadcast,
    clockState,
    matchStatus,
    sendScore: (scoreData, eventType) => webSocket.sendScoreUpdate(matchId, scoreData, eventType),
    sendEvent: (eventType, eventData) => webSocket.sendMatchEvent(matchId, eventType, eventData)
  };
};

// Hook for tournament-specific WebSocket functionality
export const useTournamentWebSocket = (tournamentId) => {
  const webSocket = useWebSocket();
  const [tournamentEvents, setTournamentEvents] = useState([]);

  // Join tournament room on mount
  useEffect(() => {
    if (tournamentId && webSocket.connected) {
      webSocket.joinTournament(tournamentId);
    }

    // Leave tournament room on unmount
    return () => {
      if (tournamentId && webSocket.connected) {
        webSocket.leaveTournament(tournamentId);
      }
    };
  }, [tournamentId, webSocket]);

  // Listen for tournament events
  useEffect(() => {
    if (!tournamentId) return;

    const removeListener = webSocket.addEventListener('tournament_score_update', (data) => {
      if (parseInt(data.tournamentId) === parseInt(tournamentId)) {
        debugLog('🏆 Tournament update:', data);
        setTournamentEvents(prev => [data, ...prev.slice(0, 99)]); // Keep last 100 events
      }
    });

    return removeListener;
  }, [tournamentId, webSocket]);

  return {
    tournamentEvents
  };
};

export default WebSocketContext;